/**
 * ModuleDetailPage — detailed view of one module.
 *
 * Shows:
 * - Module identity (id, version, tier, tags)
 * - Facet tabs (default + named facets)
 * - Dependencies (this module depends on)
 * - Dependents (modules that depend on this)
 * - Consumers (apps that use this module)
 * - Variants (forks + mirrors)
 * - Action buttons (View, Install, Fork)
 */

import { Link, useParams } from 'react-router-dom';
import { useState, useEffect } from 'react';
import {
  useModule,
  useModuleFacets,
  useModuleVariants,
  useModuleConsumers,
  useModuleDependencies,
} from '../state/gallery-store';
import { FileTree } from '../components/FileTree';
import { HumanSummary } from '../components/HumanSummary';
import './ModuleDetailPage.css';

export function ModuleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const module = useModule(id ?? null);
  const facets = useModuleFacets(id ?? null);
  const variants = useModuleVariants(id ?? null);
  const consumers = useModuleConsumers(id ?? null);
  const dependencies = useModuleDependencies(id ?? null);

  // Facet tab state — derive from URL hash so the back button works
  const [activeFacet, setActiveFacet] = useState<string>('default');
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && facets && facets[hash]) {
      setActiveFacet(hash);
    } else if (facets) {
      const first = Object.keys(facets)[0];
      if (first) setActiveFacet(first);
    }
  }, [facets]);

  if (!module) {
    return (
      <div className="module-detail">
        <p>Module not found: <code>{id}</code></p>
        <Link to="/modules">← Back to modules</Link>
      </div>
    );
  }

  const facetNames = facets ? Object.keys(facets) : [];
  const currentFacet = facets?.[activeFacet];

  return (
    <div className="module-detail">
      <Link to="/modules" className="module-detail__back">
        ← All modules
      </Link>

      <header className="module-detail__header">
        <h1 className="module-detail__id">{module.id}</h1>
        <div className="module-detail__meta">
          <span className={`module-detail__tier module-detail__tier--${module.tier}`}>
            {module.tier}
          </span>
          <span className="module-detail__version">v{module.version}</span>
          <span className="module-detail__category">{module.category}</span>
          {module.subcategory && (
            <span className="module-detail__subcategory">{module.subcategory}</span>
          )}
        </div>
        <div className="module-detail__actions">
          <a
            href={`https://github.com/avidtech6/fv-module-gallery/tree/main/modules/${module.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="module-detail__btn"
          >
            View source ↗
          </a>
          <button
            type="button"
            className="module-detail__btn module-detail__btn--primary"
            onClick={() => alert('Install: API not yet available')}
          >
            Install
          </button>
          <button
            type="button"
            className="module-detail__btn"
            onClick={() => alert('Fork: API not yet available')}
          >
            Fork
          </button>
        </div>
      </header>

      <HumanSummary
        moduleId={module.id}
        moduleName={module.id}
        moduleTags={module.tags}
      />

      {facetNames.length > 1 && (
        <div className="module-detail__facet-tabs" role="tablist">
          {facetNames.map((name) => (
            <button
              key={name}
              type="button"
              role="tab"
              aria-selected={name === activeFacet}
              className={`module-detail__facet-tab ${
                name === activeFacet ? 'module-detail__facet-tab--active' : ''
              }`}
              onClick={() => {
                setActiveFacet(name);
                window.location.hash = name;
              }}
            >
              {name}
              {facets?.[name].visibility !== 'public' && (
                <span className="module-detail__facet-private">·private</span>
              )}
            </button>
          ))}
        </div>
      )}

      {currentFacet && (
        <section className="module-detail__section">
          <h2 className="module-detail__section-title">Facet: {activeFacet}</h2>
          <div className="module-detail__facet-card">
            <dl className="module-detail__dl">
              <dt>Visibility</dt>
              <dd>
                <span className={`module-detail__vis module-detail__vis--${currentFacet.visibility}`}>
                  {currentFacet.visibility}
                </span>
              </dd>

              <dt>License</dt>
              <dd><code>{currentFacet.license}</code></dd>

              <dt>Requires</dt>
              <dd>{currentFacet.requires ?? <em>(none)</em>}</dd>

              <dt>Exports</dt>
              <dd>
                {currentFacet.exports.map((e) => (
                  <code key={e} className="module-detail__export">{e}</code>
                ))}
              </dd>

              {currentFacet.dependencies.length > 0 && (
                <>
                  <dt>Facet deps</dt>
                  <dd>
                    {currentFacet.dependencies.map((d) => (
                      <code key={d} className="module-detail__dep">{d}</code>
                    ))}
                  </dd>
                </>
              )}

              {currentFacet.consumers.length > 0 && (
                <>
                  <dt>Consumers</dt>
                  <dd>
                    {currentFacet.consumers.map((c) => (
                      <code key={c} className="module-detail__consumer">{c}</code>
                    ))}
                  </dd>
                </>
              )}
            </dl>
            {currentFacet.requires && (
              <p className="module-detail__facet-warning">
                🔒 This facet is restricted. The bundle operator must grant access
                (e.g. license key, operator agreement) before the facet is installable.
              </p>
            )}
          </div>
        </section>
      )}

      <div className="module-detail__grid">
        <section className="module-detail__section">
          <h2 className="module-detail__section-title">Dependencies</h2>
          {dependencies.length === 0 ? (
            <p className="module-detail__empty">No dependencies.</p>
          ) : (
            <ul className="module-detail__list">
              {dependencies.map((d) => (
                <li key={d}>
                  <Link to={`/modules/${d.split('@')[0]}`}>{d}</Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="module-detail__section">
          <h2 className="module-detail__section-title">Dependents</h2>
          {consumers.length === 0 ? (
            <p className="module-detail__empty">No dependents yet.</p>
          ) : (
            <ul className="module-detail__list">
              {consumers.map((c) => (
                <li key={c.app + (c.facet ?? '')}>
                  <Link to={`/modules/${c.app}`}>{c.app}</Link>
                  {c.facet && c.facet !== 'default' && (
                    <span className="module-detail__list-meta"> · {c.facet}</span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="module-detail__section">
          <h2 className="module-detail__section-title">Consumers (apps)</h2>
          {consumers.length === 0 ? (
            <p className="module-detail__empty">No apps use this module yet.</p>
          ) : (
            <ul className="module-detail__list">
              {Array.from(new Set(consumers.map((c) => c.app))).map((app) => (
                <li key={app}><code>{app}</code></li>
              ))}
            </ul>
          )}
        </section>

        <section className="module-detail__section">
          <h2 className="module-detail__section-title">Variants</h2>
          {variants.length === 0 ? (
            <p className="module-detail__empty">No forks or mirrors.</p>
          ) : (
            <ul className="module-detail__list">
              {variants.map((v) => (
                <li key={v.path} className="module-detail__variant">
                  <strong>{v.type}</strong> · <code>{v.owner}</code> ·
                  <code className="module-detail__variant-state">state: {v.state}</code>
                  {v.override_percent !== undefined && (
                    <span className="module-detail__variant-percent">
                      {' '}· {v.override_percent.toFixed(0)}% override
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <section className="module-detail__section">
        <h2 className="module-detail__section-title">Tags</h2>
        <ul className="module-detail__tags">
          {module.tags.map((t) => (
            <li key={t} className="module-detail__tag">{t}</li>
          ))}
        </ul>
      </section>

      <section className="module-detail__section">
        <h2 className="module-detail__section-title">Files</h2>
        <p className="module-detail__section-hint">
          The actual file layout of the module. Click any file to view it on GitHub.
        </p>
        <FileTree moduleId={id} />
      </section>
    </div>
  );
}
